<template>
  <div class="quiz-list-page">
    <!-- Page Header -->
    <div class="page-header">
      <div class="container-fluid">
        <div class="d-flex align-items-center justify-content-between">
          <div class="header-content">
            <div class="header-icon">
              <i class="fas fa-clipboard-list"></i>
            </div>
            <div class="header-text">
              <h1 class="page-title">
                {{ $t("qbank.my_tests") || "اختباراتي" }}
              </h1>
              <p class="page-subtitle">
                {{
                  $t("qbank.tests_subtitle") || "قائمة بكل الاختبارات المتاحة"
                }}
              </p>
            </div>
          </div>
          <button @click="$router.push('/qbank')" class="btn btn-back">
            <i class="fas fa-arrow-left"></i>
            <span>{{ $t("common.back") || "رجوع" }}</span>
          </button>
        </div>
      </div>
    </div>

    <!-- Content Section -->
    <div class="content-section">
      <div class="container-fluid">
        <!-- Loading State -->
        <div v-if="isLoading" class="loading-container">
          <div class="loading-spinner">
            <div class="spinner-ring"></div>
          </div>
          <p class="loading-text">
            {{ $t("common.loading") || "جاري التحميل..." }}
          </p>
        </div>

        <!-- Not Logged In State -->
        <div v-else-if="!isLoggedIn" class="not-logged-in">
          <div class="login-card">
            <div class="login-icon">
              <i class="fas fa-user-lock"></i>
            </div>
            <h3>
              {{ $t("auth.login_required") || "يرجى تسجيل الدخول للاستمرار" }}
            </h3>
            <a href="login" class="btn btn-login">
              <i class="fas fa-sign-in-alt"></i>
              {{ $t("auth.login") || "تسجيل الدخول" }}
            </a>
          </div>
        </div>

        <!-- Main Content -->
        <div v-else class="main-content">
          <!-- Favorites Button -->
          <div class="favorites-section">
            <button
              class="favorites-btn"
              data-bs-toggle="modal"
              data-bs-target="#show-fav-modal"
              @click="getFavorites()"
            >
              <i class="fas fa-heart"></i>
              {{ $t("qbank.favorites") || "الأسئلة المفضلة" }}
            </button>
          </div>

          <!-- Tabs Section -->
          <div class="tabs-section">
            <div class="filter-tabs">
              <button
                class="filter-tab"
                :class="{ active: activeTab === 'tests' }"
                @click="activeTab = 'tests'"
              >
                <i class="fas fa-list-alt"></i>
                {{ $t("qbank.previous_tests") || "الاختبارات السابقة" }}
              </button>
              <button
                class="filter-tab"
                :class="{ active: activeTab === 'create' }"
                @click="activeTab = 'create'"
              >
                <i class="fas fa-plus-circle"></i>
                {{ $t("qbank.new_test.create_test") || "إنشاء اختبار جديد" }}
              </button>
            </div>
          </div>

          <!-- Tab Content: Previous Tests -->
          <div v-if="activeTab === 'tests'">
            <!-- Filter Tabs - Always visible -->
            <div v-if="!testsLoading" class="test-filter-tabs">
              <button
                class="filter-chip"
                :class="{ active: testFilter === 'all' }"
                @click="setTestFilter('all')"
              >
                <i class="fas fa-list"></i>
                {{ $t("qbank.all_tests") }}
              </button>
              <button
                class="filter-chip"
                :class="{ active: testFilter === 'predefined' }"
                @click="setTestFilter('predefined')"
              >
                <i class="fas fa-file-alt"></i>
                {{ $t("qbank.predefined_tests") }}
              </button>
              <button
                class="filter-chip"
                :class="{ active: testFilter === 'ai_generated' }"
                @click="setTestFilter('ai_generated')"
              >
                <i class="fas fa-robot"></i>
                {{ $t("qbank.ai_generated_tests") }}
              </button>
              <button
                class="filter-chip"
                :class="{ active: testFilter === 'completed' }"
                @click="setTestFilter('completed')"
              >
                <i class="fas fa-check-circle"></i>
                {{ $t("qbank.completed_tests") }}
              </button>
              <button
                class="filter-chip"
                :class="{ active: testFilter === 'incomplete' }"
                @click="setTestFilter('incomplete')"
              >
                <i class="fas fa-pause-circle"></i>
                {{ $t("qbank.incomplete_tests") }}
              </button>
              <button
                class="filter-chip"
                :class="{ active: testFilter === 'archived' }"
                @click="setTestFilter('archived')"
              >
                <i class="fas fa-archive"></i>
                {{ $t("qbank.archived_tests") }}
              </button>
            </div>

            <!-- Loading Tests -->
            <div v-if="testsLoading" class="tests-loading">
              <div class="loading-spinner">
                <div class="spinner-ring"></div>
              </div>
              <p class="loading-text">
                {{ $t("qbank.loading_tests") }}
              </p>
            </div>

            <!-- Tests Grid -->
            <div v-else-if="tests && tests.length" class="tests-section">
              <h2 class="section-title">
                <i class="fas fa-list-alt"></i>
                {{ $t("qbank.available_tests") || "الاختبارات المتاحة" }}
                <span class="tests-count">{{ tests.length }}</span>
              </h2>

              <div class="tests-grid">
                <div
                  v-for="test in tests"
                  :key="test.id"
                  class="test-card"
                  :class="{ completed: test.status === 'completed' }"
                >
                  <!-- Card Header -->
                  <div class="test-card-header">
                    <div class="test-info">
                      <h3 class="test-name">
                        {{ test.name }}
                        <span class="test-id">#{{ test.id }}</span>
                      </h3>
                      <div class="test-badges">
                        <span
                          class="badge badge-mode"
                          :class="test.quiz_mode === 1 ? 'tutor' : 'timed'"
                        >
                          <i
                            :class="
                              test.quiz_mode === 1
                                ? 'fas fa-graduation-cap'
                                : 'fas fa-clock'
                            "
                          ></i>
                          {{
                            test.quiz_mode === 1
                              ? $t("qbank.test_mode_study")
                              : $t("qbank.test_mode_exam")
                          }}
                        </span>
                        <span class="badge badge-status" :class="test.status">
                          <i
                            :class="
                              test.status === 'completed'
                                ? 'fas fa-check-circle'
                                : 'fas fa-pause-circle'
                            "
                          ></i>
                          {{
                            test.status === "completed"
                              ? $t("qbank.status_completed")
                              : $t("qbank.status_paused")
                          }}
                        </span>
                      </div>
                    </div>
                    <div class="test-menu">
                      <button class="btn-menu" data-bs-toggle="dropdown">
                        <i class="fas fa-ellipsis-v"></i>
                      </button>
                      <ul class="dropdown-menu dropdown-menu-end">
                        <li v-if="test.status === 'completed'">
                          <a
                            class="dropdown-item"
                            :href="`qbank-summary?qbank=${test.id}&subscriptionId=${subscriptionId}&mode=${test.quiz_mode}`"
                          >
                            <i class="fas fa-chart-pie"></i>
                            {{ $t("qbank.performance_analysis") }}
                          </a>
                        </li>
                        <li>
                          <span
                            class="dropdown-item"
                            @click="showTestDetails(test)"
                            data-bs-toggle="modal"
                            data-bs-target="#test-details-modal"
                          >
                            <i class="fas fa-info-circle"></i>
                            {{ $t("qbank.test_details") || "تفاصيل الاختبار" }}
                          </span>
                        </li>
                        <li>
                          <span
                            class="dropdown-item"
                            @click="showNotes(test.id)"
                            data-bs-toggle="modal"
                            data-bs-target="#show-note-modal"
                          >
                            <i class="fas fa-sticky-note"></i>
                            {{ $t("qbank.view_notes") }}
                          </span>
                        </li>
                        <li>
                          <span
                            class="dropdown-item"
                            @click="showReports(test.id)"
                            data-bs-toggle="modal"
                            data-bs-target="#show-report-modal"
                          >
                            <i class="fas fa-flag"></i>
                            {{ $t("qbank.view_reports") }}
                          </span>
                        </li>
                        <li><hr class="dropdown-divider" /></li>
                        <li v-if="test.progress === 0">
                          <span
                            class="dropdown-item"
                            @click="changeMode(test.id)"
                            data-bs-toggle="modal"
                            data-bs-target="#changemode-modal"
                          >
                            <i class="fas fa-exchange-alt"></i>
                            {{ $t("qbank.change_test_type") }}
                          </span>
                        </li>
                        <li>
                          <span
                            class="dropdown-item"
                            @click="changeName(test)"
                            data-bs-toggle="modal"
                            data-bs-target="#changename-modal"
                          >
                            <i class="fas fa-edit"></i>
                            {{ $t("qbank.edit_name") }}
                          </span>
                        </li>
                        <li v-if="test.status !== 'completed'">
                          <span
                            class="dropdown-item text-success"
                            @click="finishTest(test)"
                          >
                            <i class="fas fa-check-double"></i>
                            {{ $t("qbank.finish_test") || "إنهاء الاختبار" }}
                          </span>
                        </li>
                        <li><hr class="dropdown-divider" /></li>
                        <li v-if="testFilter !== 'archived'">
                          <span
                            class="dropdown-item"
                            @click="archiveTest(test)"
                          >
                            <i class="fas fa-archive"></i>
                            {{ $t("qbank.hide_test") || "إخفاء" }}
                          </span>
                        </li>
                        <li v-if="testFilter === 'archived'">
                          <span
                            class="dropdown-item"
                            @click="unarchiveTest(test)"
                          >
                            <i class="fas fa-box-open"></i>
                            {{ $t("qbank.show_test") || "إظهار" }}
                          </span>
                        </li>
                      </ul>
                    </div>
                  </div>

                  <!-- Progress Section -->
                  <div class="test-progress">
                    <div class="progress-bar-container">
                      <div
                        class="progress-bar"
                        :style="{ width: test.progress + '%' }"
                      ></div>
                    </div>
                    <span class="progress-text">{{ test.progress }}%</span>
                  </div>

                  <!-- Stats Section -->
                  <div class="test-stats">
                    <div class="stat-item">
                      <i class="fas fa-check"></i>
                      <span class="stat-value"
                        >{{ test.correct_answers_count || 0 }}/{{
                          test.total_questions_count
                        }}</span
                      >
                      <span class="stat-label">{{
                        $t("qbank.correct_answers")
                      }}</span>
                    </div>
                    <div class="stat-item">
                      <i class="fas fa-question"></i>
                      <span class="stat-value">{{
                        test.unanswered_questions
                          ? test.unanswered_questions.length
                          : 0
                      }}</span>
                      <span class="stat-label">{{
                        $t("qbank.remaining")
                      }}</span>
                    </div>
                    <div class="stat-item">
                      <i class="fas fa-stopwatch"></i>
                      <span class="stat-value">{{
                        formatTime(test.total_time || 0)
                      }}</span>
                      <span class="stat-label">{{
                        $t("qbank.time") || "الوقت"
                      }}</span>
                    </div>
                  </div>

                  <!-- Action Buttons -->
                  <div class="test-actions">
                    <template v-if="test.status === 'completed'">
                      <button class="btn btn-review" @click="openModal(test)">
                        <i class="fas fa-eye"></i>
                        {{ $t("qbank.review_test") }}
                      </button>
                      <button
                        class="btn btn-retry"
                        @click="confirmReplicate(test)"
                      >
                        <i class="fas fa-redo"></i>
                        {{ $t("qbank.retry_test") }}
                      </button>
                    </template>
                    <template v-else>
                      <a
                        :href="
                          test.quiz_mode == 1
                            ? `qbank-quiz-tutor?qref=${test.id}&num=${test.answered_questions_count}&mode=${test.quiz_mode}&total=${test.total_questions_count}&subscriptionId=${subscriptionId}`
                            : `qbank-quiz-timed?qref=${test.id}&num=${test.answered_questions_count}&mode=${test.quiz_mode}&total=${test.total_questions_count}&subscriptionId=${subscriptionId}`
                        "
                        class="btn btn-continue"
                      >
                        <i class="fas fa-play"></i>
                        {{ $t("qbank.continue_test") }}
                      </a>
                    </template>
                  </div>
                </div>
              </div>
            </div>

            <!-- Empty State -->
            <div v-else class="empty-state">
              <div class="empty-icon">
                <i class="fas fa-clipboard"></i>
              </div>
              <h3>{{ $t("qbank.no_tests") || "لا توجد اختبارات" }}</h3>
              <p>
                {{
                  $t("qbank.no_tests_hint") || "ابدأ بإنشاء اختبار جديد الآن"
                }}
              </p>
            </div>
          </div>

          <!-- Tab Content: Create New Test -->
          <div v-if="activeTab === 'create'" class="create-test-content">
            <div class="create-test-card">
              <div class="card-icon mb-4">
                <i class="fas fa-clipboard-list"></i>
              </div>
              <h3 class="card-title mb-4">
                {{ $t("qbank.new_test.choose_type") || "اختر نوع الاختبار" }}
              </h3>

              <div class="test-options">
                <button
                  class="btn-test-option btn-custom-test"
                  @click="openCustomTest"
                >
                  <div class="option-icon">
                    <i class="fas fa-edit"></i>
                  </div>
                  <div class="option-content">
                    <h4>
                      {{
                        $t("qbank.new_test.create_custom") ||
                        "إنشاء اختبار مخصص"
                      }}
                    </h4>
                    <p>
                      {{
                        $t("qbank.new_test.custom_desc") ||
                        "اختر الأسئلة والإعدادات بنفسك"
                      }}
                    </p>
                  </div>
                  <i class="fas fa-chevron-left option-arrow"></i>
                </button>

                <button
                  class="btn-test-option btn-predefined-test"
                  @click="openPredefinedTests"
                >
                  <div class="option-icon">
                    <i class="fas fa-list-alt"></i>
                  </div>
                  <div class="option-content">
                    <h4>
                      {{
                        $t("qbank.new_test.pick_predefined") ||
                        "اختيار اختبار جاهز"
                      }}
                    </h4>
                    <p>
                      {{
                        $t("qbank.new_test.predefined_desc") ||
                        "اختبارات جاهزة ومعدة مسبقاً"
                      }}
                    </p>
                  </div>
                  <i class="fas fa-chevron-left option-arrow"></i>
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Review Modal -->
    <div class="modal fade" id="reviewModal" tabindex="-1" ref="reviewModalRef">
      <div class="modal-dialog modal-dialog-centered">
        <div class="modal-content review-modal">
          <div class="modal-header">
            <h5 class="modal-title">
              <i class="fas fa-clipboard-check"></i>
              {{ $t("qbank.review_test") }}
            </h5>
            <button
              type="button"
              class="btn-close"
              data-bs-dismiss="modal"
            ></button>
          </div>
          <div class="modal-body">
            <div class="review-options">
              <a :href="reviewLink" class="review-option">
                <div class="option-icon review-icon">
                  <i class="fas fa-search"></i>
                </div>
                <div class="option-text">
                  <span class="option-title">{{
                    $t("qbank.review_questions")
                  }}</span>
                  <span class="option-desc">{{
                    $t("qbank.review_questions_desc")
                  }}</span>
                </div>
                <i class="fas fa-chevron-left option-arrow"></i>
              </a>
              <a :href="summaryLink" class="review-option">
                <div class="option-icon summary-icon">
                  <i class="fas fa-chart-bar"></i>
                </div>
                <div class="option-text">
                  <span class="option-title">{{
                    $t("qbank.performance_analysis")
                  }}</span>
                  <span class="option-desc">{{
                    $t("qbank.performance_analysis_desc")
                  }}</span>
                </div>
                <i class="fas fa-chevron-left option-arrow"></i>
              </a>
            </div>
          </div>
          <div class="modal-footer">
            <button
              type="button"
              class="btn btn-cancel-modal"
              data-bs-dismiss="modal"
            >
              {{ $t("common.cancel") }}
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
  <!-- Modal Structure -->

  <!-- Test Details Modal -->
  <div class="modal fade" id="test-details-modal" tabindex="-1">
    <div class="modal-dialog modal-dialog-centered modal-lg">
      <div class="modal-content test-details-modal">
        <div class="modal-header">
          <h5 class="modal-title">
            <i class="fas fa-info-circle"></i>
            {{ $t("qbank.test_details") || "تفاصيل الاختبار" }}
          </h5>
          <button
            type="button"
            class="btn-close"
            data-bs-dismiss="modal"
          ></button>
        </div>
        <div class="modal-body" v-if="selectedTestDetails">
          <!-- Test Name -->
          <div class="test-details-header">
            <h4 class="test-name">
              {{ selectedTestDetails.name }}
              <span class="test-id">#{{ selectedTestDetails.id }}</span>
            </h4>
            <div class="test-badges">
              <span
                class="badge"
                :class="
                  selectedTestDetails.quiz_mode === 1
                    ? 'badge-tutor'
                    : 'badge-timed'
                "
              >
                <i
                  :class="
                    selectedTestDetails.quiz_mode === 1
                      ? 'fas fa-graduation-cap'
                      : 'fas fa-clock'
                  "
                ></i>
                {{
                  selectedTestDetails.quiz_mode === 1
                    ? $t("qbank.test_mode_study")
                    : $t("qbank.test_mode_exam")
                }}
              </span>
              <span
                class="badge"
                :class="
                  selectedTestDetails.status === 'completed'
                    ? 'badge-completed'
                    : 'badge-paused'
                "
              >
                <i
                  :class="
                    selectedTestDetails.status === 'completed'
                      ? 'fas fa-check-circle'
                      : 'fas fa-pause-circle'
                  "
                ></i>
                {{
                  selectedTestDetails.status === "completed"
                    ? $t("qbank.status_completed")
                    : $t("qbank.status_paused")
                }}
              </span>
            </div>
          </div>

          <!-- Progress Bar -->
          <div class="details-section">
            <h6 class="section-label">
              {{ $t("qbank.progress") || "التقدم" }}
            </h6>
            <div class="progress-container">
              <div class="progress-bar-wrapper">
                <div
                  class="progress-fill"
                  :style="{ width: selectedTestDetails.progress + '%' }"
                ></div>
              </div>
              <span class="progress-percentage"
                >{{ selectedTestDetails.progress }}%</span
              >
            </div>
          </div>

          <!-- Statistics Grid -->
          <div class="details-grid">
            <div class="detail-card">
              <div class="detail-icon total">
                <i class="fas fa-list-ol"></i>
              </div>
              <div class="detail-info">
                <span class="detail-value">{{
                  selectedTestDetails.total_questions_count
                }}</span>
                <span class="detail-label">{{
                  $t("qbank.total_questions") || "إجمالي الأسئلة"
                }}</span>
              </div>
            </div>

            <div class="detail-card">
              <div class="detail-icon answered">
                <i class="fas fa-check-double"></i>
              </div>
              <div class="detail-info">
                <span class="detail-value">{{
                  selectedTestDetails.answered_questions_count || 0
                }}</span>
                <span class="detail-label">{{
                  $t("qbank.answered_questions") || "الأسئلة المجابة"
                }}</span>
              </div>
            </div>

            <div class="detail-card">
              <div class="detail-icon correct">
                <i class="fas fa-check-circle"></i>
              </div>
              <div class="detail-info">
                <span class="detail-value">{{
                  selectedTestDetails.correct_answers_count || 0
                }}</span>
                <span class="detail-label">{{
                  $t("qbank.correct_answers") || "الإجابات الصحيحة"
                }}</span>
              </div>
            </div>

            <div class="detail-card">
              <div class="detail-icon incorrect">
                <i class="fas fa-times-circle"></i>
              </div>
              <div class="detail-info">
                <span class="detail-value">{{
                  selectedTestDetails.incorrect_answers_count || 0
                }}</span>
                <span class="detail-label">{{
                  $t("qbank.incorrect_answers") || "الإجابات الخاطئة"
                }}</span>
              </div>
            </div>

            <div class="detail-card">
              <div class="detail-icon remaining">
                <i class="fas fa-question-circle"></i>
              </div>
              <div class="detail-info">
                <span class="detail-value">{{
                  selectedTestDetails.unanswered_questions
                    ? selectedTestDetails.unanswered_questions.length
                    : 0
                }}</span>
                <span class="detail-label">{{
                  $t("qbank.remaining") || "الأسئلة المتبقية"
                }}</span>
              </div>
            </div>

            <div class="detail-card">
              <div class="detail-icon time">
                <i class="fas fa-stopwatch"></i>
              </div>
              <div class="detail-info">
                <span class="detail-value">{{
                  formatTime(selectedTestDetails.total_time || 0)
                }}</span>
                <span class="detail-label">{{
                  $t("qbank.time_spent") || "الوقت المستغرق"
                }}</span>
              </div>
            </div>
          </div>

          <!-- Score Section (if completed) -->
          <div
            v-if="selectedTestDetails.status === 'completed'"
            class="score-section"
          >
            <div class="score-circle">
              <span class="score-value"
                >{{ calculateScore(selectedTestDetails) }}%</span
              >
              <span class="score-label">{{
                $t("qbank.score") || "النتيجة"
              }}</span>
            </div>
          </div>
        </div>
        <div class="modal-footer">
          <button
            type="button"
            class="btn btn-secondary"
            data-bs-dismiss="modal"
          >
            {{ $t("common.close") || "إغلاق" }}
          </button>
          <a
            v-if="
              selectedTestDetails && selectedTestDetails.status === 'completed'
            "
            :href="`qbank-summary?qbank=${selectedTestDetails.id}&subscriptionId=${subscriptionId}&mode=${selectedTestDetails.quiz_mode}`"
            class="btn btn-primary"
          >
            <i class="fas fa-chart-bar"></i>
            {{ $t("qbank.view_analysis") || "عرض التحليل" }}
          </a>
        </div>
      </div>
    </div>
  </div>

  <Notes ref="notesRef" />
  <Reports ref="reportsRef" />
  <ChangeType @type-updated="refreshTests" />
  <ChangeName ref="changeNameRef" @name-updated="handleNameUpdate" />
  <NewTestDialog
    ref="newTestDialogRef"
    :subscriptionPlanId="subscriptionId"
    @refresh="refreshTests"
  />
  <Favorites ref="favRef" :subscriptionId="subscriptionId" />
</template>

<script>
import { apiUrl, baseUrl } from "@/utils/api";
import Notes from "./Notes.vue";
import Reports from "./Reports.vue";
import ChangeType from "./ChangeType.vue";
import ChangeName from "./ChangeName.vue";
import NewTestDialog from "../NewTest/NewTestDialog.vue";
import Favorites from "../Home/Favorites.vue";

export default {
  name: "QuizList",
  components: {
    Notes,
    Reports,
    ChangeType,
    ChangeName,
    NewTestDialog,
    Favorites,
  },
  data() {
    return {
      tests: [],
      categories: [],
      subCategories: [],
      qbankNotes: [],
      reviewLink: "",
      summaryLink: "",
      qbankId: "",
      isLoading: true,
      subscriptionId: this.$route.query.subscriptionId,
      token: localStorage.getItem("access_token"),
      courseCount: 0,
      isLoggedIn: false,
      walletBalance: 0,
      catImagePath: baseUrl + "/images/category/",
      defaultImage: "/assets/img/acad-icon.png",
      student: {},
      loadingCategories: true, // Loading state for categories
      loadingCourses: true, // Loading state for courses
      loadingBalance: true, // Loading state for wallet balance
      currentStep: 1, // بداية في الخطوة 1
      selectedOption: "", // البيانات للـ select box
      textInput: "", // البيانات للـ input text
      numberInput: "", // البيانات للـ input number
      isChecked: false, // البيانات للـ checkbox
      selectedTestDetails: null, // Test details for modal
      activeTab: "tests", // Active tab: 'tests' or 'create'
      testFilter: "all", // Filter for tests: 'all', 'predefined', 'ai_generated', 'completed', 'incomplete', 'archived'
      testsLoading: false, // Loading state for tests only
    };
  },

  mounted() {
    this.fetchSubscriptionDetails(this.subscriptionId);
  },
  methods: {
    toggleDropdown() {
      const dropdownMenu = document.querySelector(
        "#custom-dropdown .dropdown-menu"
      );
      // تبديل العرض بين إظهار وإخفاء
      if (dropdownMenu.style.display === "block") {
        dropdownMenu.style.display = "none";
      } else {
        dropdownMenu.style.display = "block";
      }
    },
    showTestDetails(test) {
      this.selectedTestDetails = test;
    },
    calculateScore(test) {
      if (
        !test ||
        !test.total_questions_count ||
        test.total_questions_count === 0
      ) {
        return 0;
      }
      const correct = test.correct_answers_count || 0;
      const total = test.total_questions_count;
      return Math.round((correct / total) * 100);
    },
    async showNotes(qbankNoteId) {
      document.getElementById("qbankNoteId").value = qbankNoteId;
      this.$refs.notesRef.showNotes();
    },
    async showReports(qbankReportId) {
      document.getElementById("qbankReportId").value = qbankReportId;
      this.$refs.reportsRef.showReports();
    },
    formatDate(date) {
      return new Date(date).toLocaleString("ar-EG", {
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
      });
    },
    formatTime(seconds) {
      // If time is null/undefined, return 0
      if (seconds == null) {
        return this.$i18n.locale === "ar" ? "0د" : "0m";
      }

      // Use absolute value to treat negative as positive
      const absSeconds = Math.abs(seconds);

      // If time is 0, return 0
      if (absSeconds === 0) {
        return this.$i18n.locale === "ar" ? "0د" : "0m";
      }

      const hours = Math.floor(absSeconds / 3600);
      const minutes = Math.floor((absSeconds % 3600) / 60);

      const hourUnit = this.$i18n.locale === "ar" ? "س" : "h";
      const minUnit = this.$i18n.locale === "ar" ? "د" : "m";

      if (hours > 0) {
        return `${hours}${hourUnit} ${minutes}${minUnit}`;
      }
      return `${minutes}${minUnit}`;
    },
    async fetchNotes(questionBank) {},
    async confirmFinishTest(test) {
      const unansweredCount = test.unanswered_questions ? test.unanswered_questions.length : 0;
      let warningText = this.$t("qbank.finish_test_confirm") || "هل أنت متأكد من إنهاء هذا الاختبار؟";
      
      if (unansweredCount > 0) {
        warningText += `\n${this.$t("qbank.unanswered_count") || "الأسئلة غير المجابة"}: ${unansweredCount}`;
      }

      const confirmResult = await Swal.fire({
        title: this.$t("qbank.finish_test") || "إنهاء الاختبار",
        text: warningText,
        icon: "warning",
        showCancelButton: true,
        confirmButtonText: this.$t("qbank.yes_finish") || "نعم، إنهاء",
        cancelButtonText: this.$t("common.cancel") || "إلغاء",
        confirmButtonColor: "#dc2626",
        cancelButtonColor: "#64748b",
      });

      if (!confirmResult.isConfirmed) {
        return;
      }

      Swal.fire({
        title: this.$t("qbank.finishing_test") || "جاري إنهاء الاختبار...",
        allowOutsideClick: false,
        didOpen: () => {
          Swal.showLoading();
        },
      });

      try {
        const response = await fetch(`${apiUrl}/qbanks/${test.id}/complete`, {
          method: "POST",
          headers: {
            Authorization: `Bearer ${this.token}`,
            "Content-Type": "application/json",
          },
        });

        if (response.status === 401) {
          this.handleSessionExpired();
          return;
        }

        if (response.ok) {
          Swal.fire({
            title: this.$t("qbank.success") || "تم بنجاح",
            text: this.$t("qbank.test_finished_success") || "تم إنهاء الاختبار بنجاح",
            icon: "success",
            timer: 2000,
            showConfirmButton: false,
          }).then(() => {
            // Redirect to summary page
            const summaryUrl = `qbank-summary?qbank=${test.id}&subscriptionId=${this.subscriptionId}&mode=${test.quiz_mode}`;
            window.location.href = summaryUrl;
          });
        } else {
          const errorData = await response.json().catch(() => ({}));
          throw new Error(errorData.message || this.$t("qbank.finish_test_error") || "فشل في إنهاء الاختبار");
        }
      } catch (error) {
        console.error("Error finishing test:", error);
        Swal.fire({
          title: this.$t("qbank.error") || "خطأ",
          text: error.message || this.$t("qbank.finish_test_error") || "حدث خطأ أثناء إنهاء الاختبار",
          icon: "error",
        });
      }
    },
    async archiveTest(test) {
      const confirmResult = await Swal.fire({
        title: this.$t("qbank.hide_test") || "إخفاء الاختبار",
        text: this.$t("qbank.hide_test_confirm") || "هل تريد نقل هذا الاختبار إلى الأرشيف؟",
        icon: "warning",
        showCancelButton: true,
        confirmButtonText: this.$t("qbank.hide") || "إخفاء",
        cancelButtonText: this.$t("common.cancel") || "إلغاء",
        confirmButtonColor: "#64748b",
        cancelButtonColor: "#dc2626",
      });

      if (!confirmResult.isConfirmed) {
        return;
      }

      Swal.fire({
        title: this.$t("qbank.hiding_test") || "جاري إخفاء الاختبار...",
        allowOutsideClick: false,
        didOpen: () => {
          Swal.showLoading();
        },
      });

      try {
        const response = await fetch(`${apiUrl}/qbanks/${test.id}/archive`, {
          method: "POST",
          headers: {
            Authorization: `Bearer ${this.token}`,
            "Content-Type": "application/json",
          },
        });

        if (response.status === 401) {
          this.handleSessionExpired();
          return;
        }

        if (response.ok) {
          Swal.fire({
            title: this.$t("qbank.success") || "تم بنجاح",
            text: this.$t("qbank.test_hidden_success") || "تم نقل الاختبار إلى الأرشيف",
            icon: "success",
            timer: 2000,
            showConfirmButton: false,
          });
          // Remove from current list or refresh
          this.removeTestFromList(test.id);
        } else {
          const errorData = await response.json().catch(() => ({}));
          throw new Error(errorData.message || this.$t("qbank.hide_test_error") || "فشل في إخفاء الاختبار");
        }
      } catch (error) {
        console.error("Error archiving test:", error);
        Swal.fire({
          title: this.$t("qbank.error") || "خطأ",
          text: error.message || this.$t("qbank.hide_test_error") || "حدث خطأ أثناء إخفاء الاختبار",
          icon: "error",
        });
      }
    },
    async unarchiveTest(test) {
      Swal.fire({
        title: this.$t("qbank.showing_test") || "جاري إظهار الاختبار...",
        allowOutsideClick: false,
        didOpen: () => {
          Swal.showLoading();
        },
      });

      try {
        const response = await fetch(`${apiUrl}/qbanks/${test.id}/unarchive`, {
          method: "POST",
          headers: {
            Authorization: `Bearer ${this.token}`,
            "Content-Type": "application/json",
          },
        });

        if (response.status === 401) {
          this.handleSessionExpired();
          return;
        }

        if (response.ok) {
          Swal.fire({
            title: this.$t("qbank.success") || "تم بنجاح",
            text: this.$t("qbank.test_shown_success") || "تم إظهار الاختبار بنجاح",
            icon: "success",
            timer: 2000,
            showConfirmButton: false,
          });
          // Remove from current list or refresh
          this.removeTestFromList(test.id);
        } else {
          const errorData = await response.json().catch(() => ({}));
          throw new Error(errorData.message || this.$t("qbank.show_test_error") || "فشل في إظهار الاختبار");
        }
      } catch (error) {
        console.error("Error unarchiving test:", error);
        Swal.fire({
          title: this.$t("qbank.error") || "خطأ",
          text: error.message || this.$t("qbank.show_test_error") || "حدث خطأ أثناء إظهار الاختبار",
          icon: "error",
        });
      }
    },
    removeTestFromList(testId) {
      this.tests = this.tests.filter((t) => t.id !== testId);
      // Optionally refresh the full list to sync with server
      // this.fetchSubscriptionDetails(this.subscriptionId);
    },
    async confirmReplicate(test) {
      const confirmResult = await Swal.fire({
        title: "هل أنت متأكد؟",
        text: "سيتم إعادة إنشاء هذا الاختبار من جديد!",
        icon: "warning",
        showCancelButton: true,
        confirmButtonText: "نعم، أعد الاختبار",
        cancelButtonText: "إلغاء",
        confirmButtonColor: "#00c744",
        cancelButtonColor: "#d33",
      });

      if (!confirmResult.isConfirmed) {
        return;
      }

      Swal.fire({
        title: "جاري إنشاء الاختبار...",
        text: "الرجاء الانتظار",
        allowOutsideClick: false,
        didOpen: () => {
          Swal.showLoading();
        },
      });

      try {
        const response = await fetch(`${apiUrl}/qbanks/${test.id}/replicate`, {
          method: "POST",
          headers: {
            Authorization: `Bearer ${this.token}`,
            "Content-Type": "application/json",
          },
        });

        // Handle session expiry / unauthenticated
        if (response.status === 401) {
          this.handleSessionExpired();
          return;
        }

        // Check if response is HTML (session expired redirect)
        const contentType = response.headers.get("content-type");
        if (!contentType || !contentType.includes("application/json")) {
          this.handleSessionExpired();
          return;
        }

        if (!response.ok) {
          const errorData = await response.json().catch(() => ({}));
          throw new Error(errorData.message || "فشل في إنشاء الاختبار");
        }

        const responseData = await response.json();
        const testObj = responseData.data;

        if (!testObj || !testObj.id) {
          throw new Error("لم يتم إرجاع بيانات الاختبار بشكل صحيح");
        }

        let redirectUrl = "";
        if (test.quiz_mode == 1) {
          redirectUrl = `qbank-quiz-tutor?qref=${testObj.id}&num=1&mode=${test.quiz_mode}&total=${testObj.total_questions_count}&subscriptionId=${this.subscriptionId}`;
        } else {
          redirectUrl = `qbank-quiz-timed?qref=${testObj.id}&num=1&mode=${test.quiz_mode}&total=${testObj.total_questions_count}&subscriptionId=${this.subscriptionId}`;
        }

        Swal.fire({
          title: "تم إنشاء اختبارك بنجاح!",
          text: "نتمنى لك التوفيق، جاري تحويلك للإختبار ...",
          icon: "success",
          timer: 2000,
          showConfirmButton: false,
        }).then(() => {
          window.location.href = redirectUrl;
        });
      } catch (error) {
        console.error("Error replicating test:", error);
        Swal.fire({
          title: "خطأ!",
          text:
            error.message ||
            "حدث خطأ أثناء إنشاء الاختبار. الرجاء المحاولة مرة أخرى.",
          icon: "error",
          confirmButtonText: "حسناً",
          confirmButtonColor: "#00c744",
        });
      }
    },

    openModal(test) {
      // إعداد الروابط للمراجعة والتحليل
      this.reviewLink = `qbank-quiz-review?qref=${test.id}&num=0&mode=${test.quiz_mode}&total=${test.total_questions_count}&subscriptionId=${this.subscriptionId}`;
      this.summaryLink = `qbank-summary?qbank=${test.id}&subscriptionId=${this.subscriptionId}&mode=${test.quiz_mode}`;

      // إظهار المودال
      const modal = new bootstrap.Modal(document.getElementById("reviewModal"));
      modal.show();
    },
    async changeMode(questionBank) {
      document.getElementById("qbankId").value = questionBank;
    },
    changeName(test) {
      if (this.$refs.changeNameRef) {
        this.$refs.changeNameRef.setQuestionBank(test);
      }
    },
    showNewTestDialog() {
      this.$refs.newTestDialogRef.show();
    },
    openCustomTest() {
      this.$router.push({
        path: "/qbank-create-test",
        query: { subscriptionId: this.subscriptionId },
      });
    },
    openPredefinedTests() {
      this.$router.push({
        path: "/qbank-predefined-tests",
        query: { subscriptionId: this.subscriptionId },
      });
    },
    refreshTests() {
      this.fetchSubscriptionDetails(this.subscriptionId);
    },
    async finishTest(test) {
      const result = await Swal.fire({
        title: this.$t("qbank.finish_test_confirm_title") || "إنهاء الاختبار",
        text: this.$t("qbank.finish_test_confirm_text") || "هل أنت متأكد من إنهاء هذا الاختبار؟ سيتم احتساب الأسئلة غير المجابة كإجابات خاطئة.",
        icon: "warning",
        showCancelButton: true,
        confirmButtonText: this.$t("qbank.finish") || "إنهاء",
        cancelButtonText: this.$t("common.cancel") || "إلغاء",
        confirmButtonColor: "#00c744",
        cancelButtonColor: "#6c757d",
      });

      if (!result.isConfirmed) return;

      try {
        const response = await fetch(`${apiUrl}/qbanks/${test.id}/complete`, {
          method: "POST",
          headers: {
            Authorization: `Bearer ${this.token}`,
            "Content-Type": "application/json",
          },
        });

        if (response.ok) {
          Swal.fire({
            title: this.$t("qbank.test_finished") || "تم إنهاء الاختبار",
            text: this.$t("qbank.test_finished_success") || "تم إنهاء الاختبار بنجاح",
            icon: "success",
            confirmButtonColor: "#00c744",
            timer: 2000,
            showConfirmButton: false,
          });
          // Refresh the tests list
          this.fetchSubscriptionDetails(this.subscriptionId);
        } else {
          const data = await response.json();
          Swal.fire({
            title: this.$t("common.error") || "خطأ",
            text: data.message || this.$t("qbank.finish_test_error") || "حدث خطأ أثناء إنهاء الاختبار",
            icon: "error",
            confirmButtonColor: "#00c744",
          });
        }
      } catch (error) {
        console.error("Error finishing test:", error);
        Swal.fire({
          title: this.$t("common.error") || "خطأ",
          text: this.$t("qbank.finish_test_error") || "حدث خطأ أثناء إنهاء الاختبار",
          icon: "error",
          confirmButtonColor: "#00c744",
        });
      }
    },
    getFavorites() {
      this.$refs.favRef.getFavorites();
    },
    handleNameUpdate(data) {
      const test = this.tests.find((t) => t.id == data.id);
      if (test) {
        test.name = data.name;
      }
    },
    handleSessionExpired() {
      Swal.fire({
        title: "انتهت الجلسة",
        text: "انتهت صلاحية جلستك. يرجى تسجيل الدخول مرة أخرى للمتابعة.",
        icon: "warning",
        showCancelButton: true,
        confirmButtonText: "تسجيل الدخول",
        cancelButtonText: "إلغاء",
        confirmButtonColor: "#00c744",
        cancelButtonColor: "#6c757d",
        allowOutsideClick: false,
      }).then((result) => {
        if (result.isConfirmed) {
          // Clear token and redirect to login
          localStorage.removeItem("access_token");
          window.location.href = "login";
        }
      });
    },
    setTestFilter(filter) {
      this.testFilter = filter;
      this.fetchSubscriptionDetails(this.subscriptionId);
    },
    async fetchSubscriptionDetails(subscriptionId) {
      this.testsLoading = true;
      
      // Only show page loading on initial load
      if (!this.tests || this.tests.length === 0) {
        this.isLoading = true;
      }
      
      try {
        // Build query params based on filter
        const params = new URLSearchParams();
        
        // subscription_plan_id is required
        params.append('subscription_plan_id', subscriptionId);
        
        if (this.testFilter === 'predefined') {
          params.append('predefined_only', '1');
        } else if (this.testFilter === 'ai_generated') {
          params.append('ai_generated', '1');
        } else if (this.testFilter === 'completed') {
          params.append('is_completed', '1');
        } else if (this.testFilter === 'incomplete') {
          params.append('is_completed', '0');
        } else if (this.testFilter === 'archived') {
          params.append('archived', '1');
        } else {
          // 'all' - no archived by default
          params.append('archived', '0');
        }
        
        const url = `${apiUrl}/qbanks?${params.toString()}`;

        const response = await fetch(url, {
          method: "GET",
          headers: {
            Authorization: `Bearer ${this.token}`,
            "Content-Type": "application/json",
          },
        });

        this.subscriptionId = subscriptionId;

        // Handle session expiry / unauthenticated
        if (response.status === 401) {
          this.testsLoading = false;
          this.isLoading = false;
          this.isLoggedIn = false;
          return;
        }

        if (response.ok) {
          const data = await response.json();
          this.tests = data.data || [];
          this.testsLoading = false;
          this.isLoading = false;
          this.isLoggedIn = true;
        } else {
          console.error("Failed to fetch subscription details");
          this.testsLoading = false;
          this.isLoading = false;
        }
      } catch (error) {
        console.error("Error fetching subscription details:", error);
        this.testsLoading = false;
        this.isLoading = false;
      }
    },
  },
};
</script>

<style scoped>
/* Page Layout */
.quiz-list-page {
  min-height: 100vh;
  background: linear-gradient(180deg, #f0fdf4 0%, #ecfdf5 50%, #f8fafc 100%);
}

/* Page Header */
.page-header {
  background: linear-gradient(135deg, #00c744 0%, #00a83b 100%);
  padding: 24px 20px;
  color: white;
}

.header-content {
  display: flex;
  align-items: center;
  gap: 16px;
}

.header-icon {
  width: 50px;
  height: 50px;
  background: rgba(255, 255, 255, 0.2);
  border-radius: 14px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 22px;
}

.header-icon i {
  color: white !important;
}

.page-title {
  font-size: 1.4rem;
  font-weight: 700;
  margin: 0;
}

.page-subtitle {
  font-size: 0.85rem;
  opacity: 0.9;
  margin: 4px 0 0;
}

.btn-back {
  background: rgba(255, 255, 255, 0.2);
  color: white;
  border: none;
  padding: 10px 20px;
  border-radius: 10px;
  font-weight: 600;
  display: flex;
  align-items: center;
  gap: 8px;
  transition: all 0.2s ease;
}

.btn-back:hover {
  background: rgba(255, 255, 255, 0.3);
  color: white;
}

.btn-back i {
  color: white !important;
}

/* Content Section */
.content-section {
  padding: 24px 0;
}

/* Loading State */
.loading-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 300px;
}

.loading-spinner {
  position: relative;
  width: 50px;
  height: 50px;
}

.spinner-ring {
  width: 50px;
  height: 50px;
  border: 4px solid #e2e8f0;
  border-top-color: #00c744;
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

.loading-text {
  margin-top: 16px;
  color: #64748b;
  font-weight: 600;
}

/* Not Logged In */
.not-logged-in {
  display: flex;
  justify-content: center;
  padding: 40px 20px;
}

.login-card {
  background: white;
  border-radius: 20px;
  padding: 40px;
  text-align: center;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
  max-width: 400px;
}

.login-icon {
  width: 80px;
  height: 80px;
  background: #00c744;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 20px;
  font-size: 32px;
}

.login-icon i {
  color: white !important;
}

.login-card h3 {
  color: #1e293b;
  font-weight: 700;
  margin-bottom: 20px;
}

.btn-login {
  background: #00c744;
  color: white;
  padding: 12px 32px;
  border-radius: 10px;
  font-weight: 600;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  transition: all 0.2s ease;
}

.btn-login:hover {
  background: #00a83b;
  color: white;
}

.btn-login i {
  color: white !important;
}

/* Favorites Section */
.favorites-section {
  margin-bottom: 20px;
  display: flex;
  justify-content: flex-start;
}

.favorites-btn {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 12px 24px;
  background: linear-gradient(135deg, #00c744 0%, #00a83b 100%);
  color: white;
  border: none;
  border-radius: 12px;
  font-weight: 600;
  font-size: 0.95rem;
  box-shadow: 0 6px 20px rgba(0, 199, 68, 0.3);
  transition: all 0.3s ease;
  cursor: pointer;
}

.favorites-btn i {
  color: white;
}

.favorites-btn:hover {
  transform: translateY(-2px);
  box-shadow: 0 10px 30px rgba(0, 199, 68, 0.4);
}

/* Tabs Section */
.tabs-section {
  margin-bottom: 24px;
}

.filter-tabs {
  display: flex;
  background: white;
  border-radius: 12px;
  padding: 4px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
  gap: 4px;
}

.filter-tab {
  flex: 1;
  padding: 12px 20px;
  border: none;
  background: transparent;
  border-radius: 10px;
  font-weight: 600;
  color: #64748b;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  transition: all 0.2s ease;
  cursor: pointer;
}

.filter-tab:hover {
  background: #f1f5f9;
}

.filter-tab.active {
  background: #00c744;
  color: white;
}

.filter-tab.active i {
  color: white !important;
}

.filter-tab i {
  font-size: 14px;
}

/* Create Test Content */
.create-test-content {
  display: flex;
  justify-content: center;
  padding: 40px 20px;
}

.create-test-card {
  background: white;
  border-radius: 24px;
  padding: 48px;
  max-width: 700px;
  width: 100%;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
  text-align: center;
}

.card-icon {
  width: 80px;
  height: 80px;
  background: linear-gradient(135deg, #00c744, #00a83b);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto;
}

.card-icon i {
  font-size: 36px;
  color: white !important;
}

.card-title {
  font-size: 1.5rem;
  font-weight: 700;
  color: #1f2937;
}

.test-options {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.btn-test-option {
  display: flex;
  align-items: center;
  gap: 20px;
  padding: 24px;
  background: white;
  border: 2px solid #e5e7eb;
  border-radius: 16px;
  cursor: pointer;
  transition: all 0.3s ease;
  text-align: right;
}

.btn-test-option:hover {
  border-color: #00c744;
  background: #f0fdf4;
  transform: translateY(-2px);
  box-shadow: 0 8px 24px rgba(0, 199, 68, 0.15);
}

.option-icon {
  width: 56px;
  height: 56px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.btn-custom-test .option-icon {
  background: linear-gradient(135deg, #3b82f6, #2563eb);
}

.btn-predefined-test .option-icon {
  background: linear-gradient(135deg, #8b5cf6, #7c3aed);
}

.option-icon i {
  font-size: 24px;
  color: white !important;
}

.option-content {
  flex: 1;
  text-align: right;
}

.option-content h4 {
  margin: 0 0 4px;
  font-size: 1.1rem;
  font-weight: 600;
  color: #1f2937;
}

.option-content p {
  margin: 0;
  font-size: 0.9rem;
  color: #6b7280;
}

.option-arrow {
  font-size: 20px;
  color: #9ca3af;
  transition: all 0.3s ease;
}

.btn-test-option:hover .option-arrow {
  color: #00c744;
  transform: translateX(-4px);
}

[dir="rtl"] .btn-test-option:hover .option-arrow {
  transform: translateX(4px);
}

/* Create Test Button */
.create-test-section {
  margin-bottom: 24px;
}

.btn-create-test {
  width: 100%;
  background: white;
  border: 2px dashed #00c744;
  border-radius: 16px;
  padding: 20px;
  display: flex;
  align-items: center;
  gap: 16px;
  transition: all 0.2s ease;
  text-align: right;
}

.btn-create-test:hover {
  background: #f0fdf4;
  border-style: solid;
}

.btn-create-test .btn-icon {
  width: 50px;
  height: 50px;
  background: #00c744;
  border-radius: 14px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 20px;
  flex-shrink: 0;
}

.btn-create-test .btn-icon i {
  color: white !important;
}

.btn-create-test .btn-text {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.btn-create-test .btn-title {
  font-size: 1.1rem;
  font-weight: 700;
  color: #1e293b;
}

.btn-create-test .btn-subtitle {
  font-size: 0.85rem;
  color: #64748b;
}

.btn-create-test .btn-arrow {
  color: #00c744;
  font-size: 18px;
}

/* Section Title */
.section-title {
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: 1.1rem;
  font-weight: 700;
  color: #1e293b;
  margin-bottom: 20px;
}

.section-title i {
  color: #00c744;
}

.tests-count {
  background: #00c744;
  color: white;
  padding: 4px 12px;
  border-radius: 20px;
  font-size: 0.8rem;
  font-weight: 600;
}

/* Tests Loading State */
.tests-loading {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 80px 20px;
  background: white;
  border-radius: 24px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.05);
}

.tests-loading .loading-spinner {
  margin-bottom: 20px;
}

.tests-loading .loading-text {
  color: #64748b;
  font-size: 1rem;
  font-weight: 500;
  margin: 0;
}

/* Test Filter Tabs */
.test-filter-tabs {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  margin-bottom: 20px;
  padding: 15px;
  background: white;
  border-radius: 16px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
}

.tests-section .test-filter-tabs {
  margin-bottom: 25px;
}

.filter-chip {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 10px 18px;
  background: #f8fafc;
  border: 2px solid #e2e8f0;
  border-radius: 12px;
  font-size: 0.9rem;
  font-weight: 600;
  color: #64748b;
  cursor: pointer;
  transition: all 0.3s ease;
}

.filter-chip:hover {
  background: #e8fff1;
  border-color: #00c744;
  color: #00c744;
  transform: translateY(-2px);
}

.filter-chip.active {
  background: linear-gradient(135deg, #00c744 0%, #00a83b 100%);
  border-color: #00c744;
  color: white;
  box-shadow: 0 4px 12px rgba(0, 199, 68, 0.3);
}

.filter-chip i {
  font-size: 1rem;
}

/* Tests Grid */
.tests-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(340px, 1fr));
  gap: 20px;
}

/* Test Card */
.test-card {
  background: white;
  border-radius: 16px;
  padding: 20px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.06);
  transition: all 0.2s ease;
  border: 2px solid transparent;
}

.test-card:hover {
  box-shadow: 0 8px 30px rgba(0, 199, 68, 0.12);
  border-color: #00c744;
}

.test-card.completed {
  border-right: 4px solid #00c744;
}

.test-card-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 16px;
}

.test-name {
  font-size: 1rem;
  font-weight: 700;
  color: #1e293b;
  margin: 0 0 10px;
}

.test-id {
  font-size: 0.8rem;
  font-weight: 500;
  color: #94a3b8;
  margin-inline-start: 6px;
}

.test-badges {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.badge {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 4px 10px;
  border-radius: 6px;
  font-size: 0.75rem;
  font-weight: 600;
}

.badge-mode.tutor {
  background: #f0fdf4;
  color: #15803d;
}

.badge-mode.timed {
  background: #eff6ff;
  color: #1d4ed8;
}

.badge-status.completed {
  background: #dcfce7;
  color: #166534;
}

.badge-status.paused,
.badge-status.active {
  background: #fef3c7;
  color: #92400e;
}

.btn-menu {
  width: 36px;
  height: 36px;
  background: #f8fafc;
  border: none;
  border-radius: 8px;
  color: #64748b;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s ease;
}

.btn-menu:hover {
  background: #e2e8f0;
  color: #1e293b;
}

.dropdown-menu {
  border: none;
  border-radius: 12px;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.15);
  padding: 8px;
}

.dropdown-item {
  padding: 10px 14px;
  border-radius: 8px;
  font-size: 0.9rem;
  display: flex;
  align-items: center;
  gap: 10px;
  cursor: pointer;
}

.dropdown-item:hover {
  background: #f0fdf4;
  color: #00c744;
}

.dropdown-item i {
  width: 18px;
  text-align: center;
  color: #64748b;
}

.dropdown-item:hover i {
  color: #00c744;
}

/* Progress */
.test-progress {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 16px;
}

.progress-bar-container {
  flex: 1;
  height: 8px;
  background: #e2e8f0;
  border-radius: 4px;
  overflow: hidden;
}

.progress-bar {
  height: 100%;
  background: linear-gradient(90deg, #00c744, #10b981);
  border-radius: 4px;
  transition: width 0.3s ease;
}

.progress-text {
  font-size: 0.85rem;
  font-weight: 700;
  color: #00c744;
  min-width: 40px;
}

/* Stats */
.test-stats {
  display: flex;
  justify-content: space-between;
  padding: 14px;
  background: #f8fafc;
  border-radius: 12px;
  margin-bottom: 16px;
}

.stat-item {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
}

.stat-item i {
  color: #00c744;
  font-size: 14px;
}

.stat-value {
  font-size: 1rem;
  font-weight: 700;
  color: #1e293b;
}

.stat-label {
  font-size: 0.7rem;
  color: #64748b;
}

/* Action Buttons */
.test-actions {
  display: flex;
  gap: 10px;
}

.btn-review,
.btn-continue {
  flex: 1;
  padding: 12px 16px;
  background: #00c744;
  color: white;
  border: none;
  border-radius: 10px;
  font-weight: 600;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  transition: all 0.2s ease;
  text-decoration: none;
}

.btn-review:hover,
.btn-continue:hover {
  background: #00a83b;
  color: white;
}

.btn-review i,
.btn-continue i {
  color: white !important;
}

.btn-retry {
  padding: 12px 16px;
  background: #f8fafc;
  color: #64748b;
  border: none;
  border-radius: 10px;
  font-weight: 600;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  transition: all 0.2s ease;
}

.btn-retry:hover {
  background: #e2e8f0;
  color: #1e293b;
}

/* Empty State */
.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 60px 20px;
  text-align: center;
}

.empty-icon {
  width: 80px;
  height: 80px;
  background: #00c744;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 20px;
  font-size: 32px;
}

.empty-icon i {
  color: white !important;
}

.empty-state h3 {
  color: #1e293b;
  font-weight: 700;
  margin-bottom: 8px;
}

.empty-state p {
  color: #64748b;
  margin: 0;
}

/* Review Modal */
.review-modal {
  border-radius: 20px;
  border: none;
  overflow: hidden;
}

.review-modal .modal-header {
  background: linear-gradient(135deg, #00c744 0%, #00a83b 100%);
  color: white;
  border: none;
  padding: 18px 24px;
}

.review-modal .modal-header .btn-close {
  filter: brightness(0) invert(1);
}

.review-modal .modal-title {
  display: flex;
  align-items: center;
  gap: 10px;
  font-weight: 700;
}

.review-modal .modal-title i {
  color: white !important;
}

.review-modal .modal-body {
  padding: 24px;
}

.review-options {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.review-option {
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 16px;
  background: #f8fafc;
  border-radius: 12px;
  text-decoration: none;
  transition: all 0.2s ease;
}

.review-option:hover {
  background: #f0fdf4;
}

.option-icon {
  width: 46px;
  height: 46px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 18px;
}

.option-icon i {
  color: white !important;
}

.review-icon {
  background: #00c744;
}

.summary-icon {
  background: #10b981;
}

.option-text {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.option-title {
  font-weight: 600;
  color: #1e293b;
}

.option-desc {
  font-size: 0.8rem;
  color: #64748b;
}

.option-arrow {
  color: #94a3b8;
}

.review-modal .modal-footer {
  border: none;
  padding: 16px 24px;
  background: #f8fafc;
}

.btn-cancel-modal {
  width: 100%;
  padding: 12px;
  background: #e2e8f0;
  color: #64748b;
  border: none;
  border-radius: 10px;
  font-weight: 600;
}

.btn-cancel-modal:hover {
  background: #cbd5e1;
  color: #475569;
}

/* Dark Mode */
[data-bs-theme="dark"] .quiz-list-page,
.dark .quiz-list-page {
  background: linear-gradient(180deg, #0f172a 0%, #1e293b 100%);
}

[data-bs-theme="dark"] .test-card,
.dark .test-card {
  background: #1e293b;
}

[data-bs-theme="dark"] .test-name,
.dark .test-name {
  color: #f1f5f9;
}

[data-bs-theme="dark"] .test-id,
.dark .test-id {
  color: #64748b;
}

[data-bs-theme="dark"] .btn-create-test,
.dark .btn-create-test {
  background: #1e293b;
  border-color: #00c744;
}

[data-bs-theme="dark"] .btn-create-test .btn-title,
.dark .btn-create-test .btn-title {
  color: #f1f5f9;
}

[data-bs-theme="dark"] .btn-create-test .btn-subtitle,
.dark .btn-create-test .btn-subtitle {
  color: #94a3b8;
}

[data-bs-theme="dark"] .section-title,
.dark .section-title {
  color: #f1f5f9;
}

[data-bs-theme="dark"] .tests-loading,
.dark .tests-loading {
  background: #1e293b;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
}

[data-bs-theme="dark"] .tests-loading .loading-text,
.dark .tests-loading .loading-text {
  color: #94a3b8;
}

[data-bs-theme="dark"] .test-filter-tabs,
.dark .test-filter-tabs {
  background: #1e293b;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.3);
}

[data-bs-theme="dark"] .filter-chip,
.dark .filter-chip {
  background: #0f172a;
  border-color: #334155;
  color: #94a3b8;
}

[data-bs-theme="dark"] .filter-chip:hover,
.dark .filter-chip:hover {
  background: #1e3a1e;
  border-color: #4ade80;
  color: #4ade80;
}

[data-bs-theme="dark"] .filter-chip.active,
.dark .filter-chip.active {
  background: linear-gradient(135deg, #4ade80 0%, #22c55e 100%);
  border-color: #4ade80;
  color: #0f172a;
  box-shadow: 0 4px 12px rgba(74, 222, 128, 0.3);
}

[data-bs-theme="dark"] .test-stats,
.dark .test-stats {
  background: #334155;
}

[data-bs-theme="dark"] .stat-value,
.dark .stat-value {
  color: #f1f5f9;
}

[data-bs-theme="dark"] .btn-menu,
.dark .btn-menu {
  background: #334155;
  color: #94a3b8;
}

[data-bs-theme="dark"] .btn-retry,
.dark .btn-retry {
  background: #334155;
  color: #94a3b8;
}

[data-bs-theme="dark"] .progress-bar-container,
.dark .progress-bar-container {
  background: #334155;
}

[data-bs-theme="dark"] .login-card,
.dark .login-card {
  background: #1e293b;
}

[data-bs-theme="dark"] .login-card h3,
.dark .login-card h3 {
  color: #f1f5f9;
}

[data-bs-theme="dark"] .empty-state h3,
.dark .empty-state h3 {
  color: #f1f5f9;
}

[data-bs-theme="dark"] .review-modal,
.dark .review-modal {
  background: #1e293b;
}

[data-bs-theme="dark"] .review-option,
.dark .review-option {
  background: #334155;
}

[data-bs-theme="dark"] .option-title,
.dark .option-title {
  color: #f1f5f9;
}

[data-bs-theme="dark"] .review-modal .modal-footer,
.dark .review-modal .modal-footer {
  background: #1e293b;
}

[data-bs-theme="dark"] .btn-cancel-modal,
.dark .btn-cancel-modal {
  background: #334155;
  color: #94a3b8;
}

[data-bs-theme="dark"] .dropdown-menu,
.dark .dropdown-menu {
  background: #1e293b;
}

[data-bs-theme="dark"] .dropdown-item,
.dark .dropdown-item {
  color: #e2e8f0;
}

[data-bs-theme="dark"] .dropdown-item:hover,
.dark .dropdown-item:hover {
  background: #334155;
}

[data-bs-theme="dark"] .favorites-btn,
.dark .favorites-btn {
  background: linear-gradient(135deg, #0f5132 0%, #0a3622 100%);
  box-shadow: 0 6px 20px rgba(0, 0, 0, 0.3);
}

[data-bs-theme="dark"] .favorites-btn:hover,
.dark .favorites-btn:hover {
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.4);
}

/* Dark Mode - Filter Tabs (Tests/Create tabs) */
[data-bs-theme="dark"] .filter-tabs,
.dark .filter-tabs {
  background: #1e293b;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.3);
}

[data-bs-theme="dark"] .filter-tab,
.dark .filter-tab {
  background: transparent;
  color: #94a3b8;
}

[data-bs-theme="dark"] .filter-tab:hover,
.dark .filter-tab:hover {
  background: #334155;
  color: #e2e8f0;
}

[data-bs-theme="dark"] .filter-tab.active,
.dark .filter-tab.active {
  background: #00c744;
  color: white;
}

[data-bs-theme="dark"] .filter-tab i,
.dark .filter-tab i {
  color: #94a3b8;
}

[data-bs-theme="dark"] .filter-tab.active i,
.dark .filter-tab.active i {
  color: white !important;
}

/* Dark Mode - Create Test Card */
[data-bs-theme="dark"] .create-test-card,
.dark .create-test-card {
  background: #1e293b;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.3);
}

[data-bs-theme="dark"] .create-test-card .card-icon,
.dark .create-test-card .card-icon {
  background: linear-gradient(135deg, #0f5132 0%, #0a3622 100%);
}

[data-bs-theme="dark"] .create-test-card .card-title,
.dark .create-test-card .card-title {
  color: #f1f5f9;
}

[data-bs-theme="dark"] .btn-test-option,
.dark .btn-test-option {
  background: #0f172a;
  border-color: #334155;
}

[data-bs-theme="dark"] .btn-test-option:hover,
.dark .btn-test-option:hover {
  background: #1e3a1e;
  border-color: #4ade80;
}

[data-bs-theme="dark"] .btn-test-option .option-icon,
.dark .btn-test-option .option-icon {
  background: #334155;
  color: #4ade80;
}

[data-bs-theme="dark"] .btn-test-option:hover .option-icon,
.dark .btn-test-option:hover .option-icon {
  background: #22c55e;
  color: white;
}

[data-bs-theme="dark"] .btn-test-option h4,
.dark .btn-test-option h4 {
  color: #f1f5f9;
}

[data-bs-theme="dark"] .btn-test-option p,
.dark .btn-test-option p {
  color: #94a3b8;
}

[data-bs-theme="dark"] .btn-test-option .option-arrow,
.dark .btn-test-option .option-arrow {
  color: #64748b;
}

[data-bs-theme="dark"] .btn-test-option:hover .option-arrow,
.dark .btn-test-option:hover .option-arrow {
  color: #4ade80;
}

/* Responsive */
@media (max-width: 768px) {
  .page-header {
    padding: 18px 15px;
  }

  .header-icon {
    width: 44px;
    height: 44px;
    font-size: 18px;
  }

  .page-title {
    font-size: 1.2rem;
  }

  .tests-grid {
    grid-template-columns: 1fr;
  }

  .btn-back span {
    display: none;
  }

  .test-stats {
    flex-wrap: wrap;
    gap: 12px;
  }

  .stat-item {
    flex: 1;
    min-width: 80px;
  }
}

/* RTL Support */
[dir="rtl"] .btn-back i,
[dir="rtl"] .btn-arrow,
[dir="rtl"] .option-arrow {
  transform: rotate(180deg);
}

[dir="rtl"] .test-card.completed {
  border-right: none;
  border-left: 4px solid #00c744;
}

/* Test Details Modal Styles */
.test-details-modal {
  border-radius: 16px;
  overflow: hidden;
}

.test-details-modal .modal-header {
  background: linear-gradient(135deg, #00c744 0%, #00a83b 100%);
  color: white;
  border: none;
  padding: 18px 24px;
}

.test-details-modal .modal-title {
  font-weight: 600;
  display: flex;
  align-items: center;
  gap: 10px;
}

.test-details-modal .modal-title i {
  color: white !important;
}

.test-details-modal .btn-close {
  filter: brightness(0) invert(1);
}

.test-details-modal .modal-body {
  padding: 24px;
}

.test-details-header {
  text-align: center;
  margin-bottom: 24px;
  padding-bottom: 20px;
  border-bottom: 1px solid #e2e8f0;
}

.test-details-header .test-name {
  font-size: 1.3rem;
  font-weight: 700;
  color: #1e293b;
  margin-bottom: 12px;
}

.test-details-header .test-badges {
  display: flex;
  justify-content: center;
  gap: 10px;
  flex-wrap: wrap;
}

.test-details-header .badge {
  padding: 6px 14px;
  border-radius: 20px;
  font-size: 0.8rem;
  font-weight: 600;
  display: flex;
  align-items: center;
  gap: 6px;
}

.badge-tutor {
  background: #dbeafe;
  color: #1d4ed8;
}

.badge-timed {
  background: #fef3c7;
  color: #d97706;
}

.badge-completed {
  background: #dcfce7;
  color: #16a34a;
}

.badge-paused {
  background: #fef9c3;
  color: #ca8a04;
}

.details-section {
  margin-bottom: 24px;
}

.section-label {
  font-weight: 600;
  color: #64748b;
  margin-bottom: 10px;
  font-size: 0.9rem;
}

.progress-container {
  display: flex;
  align-items: center;
  gap: 14px;
}

.progress-bar-wrapper {
  flex: 1;
  height: 10px;
  background: #e2e8f0;
  border-radius: 10px;
  overflow: hidden;
}

.progress-fill {
  height: 100%;
  background: linear-gradient(90deg, #00c744 0%, #10b981 100%);
  border-radius: 10px;
  transition: width 0.3s ease;
}

.progress-percentage {
  font-weight: 700;
  color: #00c744;
  font-size: 1rem;
  min-width: 45px;
}

.details-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 14px;
  margin-bottom: 24px;
}

.detail-card {
  background: #f8fafc;
  border-radius: 12px;
  padding: 16px;
  display: flex;
  align-items: center;
  gap: 12px;
}

.detail-icon {
  width: 42px;
  height: 42px;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 16px;
}

.detail-icon i {
  color: white !important;
}

.detail-icon.total {
  background: #6366f1;
}

.detail-icon.answered {
  background: #3b82f6;
}

.detail-icon.correct {
  background: #10b981;
}

.detail-icon.incorrect {
  background: #ef4444;
}

.detail-icon.remaining {
  background: #f59e0b;
}

.detail-icon.time {
  background: #8b5cf6;
}

.detail-info {
  display: flex;
  flex-direction: column;
}

.detail-value {
  font-size: 1.2rem;
  font-weight: 700;
  color: #1e293b;
}

.detail-label {
  font-size: 0.75rem;
  color: #64748b;
}

.score-section {
  display: flex;
  justify-content: center;
  padding-top: 20px;
  border-top: 1px solid #e2e8f0;
}

.score-circle {
  width: 100px;
  height: 100px;
  border-radius: 50%;
  background: linear-gradient(135deg, #00c744 0%, #10b981 100%);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  color: white;
}

.score-value {
  font-size: 1.6rem;
  font-weight: 700;
}

.score-label {
  font-size: 0.75rem;
  opacity: 0.9;
}

.test-details-modal .modal-footer {
  border: none;
  padding: 16px 24px;
  background: #f8fafc;
  gap: 10px;
}

.test-details-modal .btn-secondary {
  background: #e2e8f0;
  color: #64748b;
  border: none;
  padding: 10px 20px;
  border-radius: 10px;
  font-weight: 600;
}

.test-details-modal .btn-secondary:hover {
  background: #cbd5e1;
  color: #475569;
}

.test-details-modal .btn-primary {
  background: linear-gradient(135deg, #00c744 0%, #00a83b 100%);
  border: none;
  padding: 10px 20px;
  border-radius: 10px;
  font-weight: 600;
  display: flex;
  align-items: center;
  gap: 8px;
}

.test-details-modal .btn-primary:hover {
  background: linear-gradient(135deg, #00a83b 0%, #008830 100%);
}

/* Dark mode for Test Details Modal */
[data-bs-theme="dark"] .test-details-modal .modal-content,
.dark .test-details-modal .modal-content {
  background: #1e293b;
}

[data-bs-theme="dark"] .test-details-header .test-name,
.dark .test-details-header .test-name {
  color: #f1f5f9;
}

[data-bs-theme="dark"] .test-details-header,
.dark .test-details-header {
  border-bottom-color: #334155;
}

[data-bs-theme="dark"] .detail-card,
.dark .detail-card {
  background: #334155;
}

[data-bs-theme="dark"] .detail-value,
.dark .detail-value {
  color: #f1f5f9;
}

[data-bs-theme="dark"] .progress-bar-wrapper,
.dark .progress-bar-wrapper {
  background: #334155;
}

[data-bs-theme="dark"] .score-section,
.dark .score-section {
  border-top-color: #334155;
}

[data-bs-theme="dark"] .test-details-modal .modal-footer,
.dark .test-details-modal .modal-footer {
  background: #1e293b;
}

[data-bs-theme="dark"] .test-details-modal .btn-secondary,
.dark .test-details-modal .btn-secondary {
  background: #334155;
  color: #94a3b8;
}

/* Responsive for Test Details Modal */
@media (max-width: 768px) {
  .details-grid {
    grid-template-columns: repeat(2, 1fr);
  }

  .detail-card {
    padding: 12px;
  }

  .detail-icon {
    width: 36px;
    height: 36px;
    font-size: 14px;
  }

  .detail-value {
    font-size: 1rem;
  }
}

@media (max-width: 480px) {
  .details-grid {
    grid-template-columns: 1fr 1fr;
  }

  .test-details-header .test-badges {
    flex-direction: column;
    align-items: center;
  }
}
</style>
